/**
 * Names JSS rules after their content instead of a counter.
 *
 * MUI's own createGenerateClassName ends every production class in a number
 * from a module-level counter that only ever increments, so a rule's class
 * name depends on how many rules happened to be created before it. Remounting
 * a component gives it a new name (mui-jss-mui-jss46 became ...94 then ...142
 * across two round trips of the mobile webshop), which breaks anything holding
 * on to a class between renders - VWO campaigns above all, since the Visual
 * Editor targets whatever class the element had when the change was authored.
 *
 * Hashing the rule's content instead makes the name a function of the CSS, so
 * it survives remounts, sessions and deploys that leave the styles alone.
 */
/**
 * Rule names MUI maps onto its global pseudo-class helpers. Copied from
 * @mui/styles/createGenerateClassName so Mui-prefixed sheets keep the exact
 * names the library and its consumers expect.
 */
const STATE_CLASSES = [
    "checked",
    "disabled",
    "error",
    "focused",
    "focusVisible",
    "required",
    "expanded",
    "selected",
];
/** Kept from the previous generator: isolates us from other JSS bundles. */
const SEED = "mui-jss";
/** Only ever used for the rules that must not share a name - see below. */
let instanceCounter = 0;
/**
 * FNV-1a, base36. A weak hash is fine here: two rules can only collide when
 * their key and CSS are identical, in which case the shared class renders the
 * same declarations. JSS gives every sheet its own <style> element, so one
 * sheet detaching cannot strip a class another sheet is still rendering.
 */
function hash(input) {
    let value = 0x811c9dc5;
    for (let i = 0; i < input.length; i++) {
        value ^= input.charCodeAt(i);
        value = Math.imul(value, 0x01000193);
    }
    return (value >>> 0).toString(36);
}
/**
 * The rule's own name, kept in the class so that a JSS class says something
 * about what it styles. Whoever picks an element in VWO's editor reads this,
 * and mui-jss-loginButton-1a2b3c is a great deal more use to them than the
 * bare hash. Rule keys are authored object keys, so this only guards against
 * the exotic ones ending up in a selector.
 */
function readable(key) {
    return key.replace(/[^A-Za-z0-9_-]/g, "-").slice(0, 32);
}
/**
 * The @media / @supports chain the rule sits inside, plus any media the whole
 * sheet was built under. Part of the name because the declarations alone do not
 * identify a rule: two sheets each holding `hidden: {display: none}` under a
 * different @media would otherwise share a class, and that one class would then
 * take both conditions. Empty for the ordinary top-level rule, which is what
 * lets those keep the names they had before this was introduced - see the note
 * on renaming in generateClassName.
 *
 * Nested @media - the form MUI code actually writes, inside a rule rather than
 * around it - never reaches here: JSS reuses the parent rule's class for it and
 * skips id generation entirely.
 */
function conditions(rule, sheet) {
    var _a, _b, _c;
    const keys = ((_a = sheet === null || sheet === void 0 ? void 0 : sheet.options) === null || _a === void 0 ? void 0 : _a.media) ? [sheet.options.media] : [];
    let parent = (_b = rule.options) === null || _b === void 0 ? void 0 : _b.parent;
    while (parent && typeof parent.key === "string") {
        keys.push(parent.key);
        parent = (_c = parent.options) === null || _c === void 0 ? void 0 : _c.parent;
    }
    return keys.join("|");
}
export const generateClassName = (rule, sheet) => {
    const options = sheet === null || sheet === void 0 ? void 0 : sheet.options;
    const name = options === null || options === void 0 ? void 0 : options.name;
    // MUI's own global sheets keep MUI's naming. Those classes are a public API
    // (MuiButton-containedPrimary and the rest) and are already content-stable.
    if ((name === null || name === void 0 ? void 0 : name.startsWith("Mui")) && !(options === null || options === void 0 ? void 0 : options.link)) {
        if (STATE_CLASSES.includes(rule.key)) {
            return `Mui-${rule.key}`;
        }
        return `${SEED}-${name}-${rule.key}`;
    }
    const style = rule.style;
    // Two kinds of rule cannot be named after their content. A linked sheet is
    // makeStyles' per-instance dynamic styles, where sharing a class would let
    // one instance restyle another from its own props. A rule with no style
    // object is @keyframes and the like, whose id never lands on an element as a
    // class, so a counter there costs nothing and avoids collapsing two
    // same-named animations onto one definition.
    if ((options === null || options === void 0 ? void 0 : options.link) || !style) {
        instanceCounter += 1;
        return `${SEED}-dyn${instanceCounter}`;
    }
    // A rule under no condition is hashed exactly as it was before conditions
    // were taken into account, so that adding them renamed nothing. Renaming is
    // not a free refactor here: a live VWO campaign holds the old class in its
    // selector and silently stops matching the moment the name moves, which is
    // the failure this whole generator exists to prevent. Treat every input to
    // this hash as a published contract - generateClassName.test.ts pins the
    // exact names so that changing one has to be deliberate.
    const condition = conditions(rule, sheet);
    const identity = condition
        ? condition + "\n" + rule.key + "\n" + JSON.stringify(style)
        : rule.key + JSON.stringify(style);
    return `${SEED}-${readable(rule.key)}-${hash(identity)}`;
};
export default generateClassName;
